import React from 'react';
import { toast } from 'react-hot-toast';
import isEmpty from 'lodash/isEmpty';
import { useFormikContext } from 'formik';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { useGetIsPartner } from '@project/queries';
import { IndustryField } from '@project/components/molecules';
import { Button, Dropdown, TextField } from '@project/components/atoms';
import { PartnerRegisterationValues } from '@project/schemas';
import { CountryCode } from 'libphonenumber-js/core';
import {
  EMAIL_ALREADY_EXIST,
  EMAIL_ALREADY_EXIST_GUEST,
  FILL_REQUIRED_INPUTS,
  TOAST_DURATION,
} from '@project/helper';
import { getCode, getNames } from 'country-list';
import { BaseOption } from '@project/types';

const countries = getNames();

const countryOptions: BaseOption[] = countries
  .map((country: string) => ({
    label: country,
    value: getCode(country) || '',
  }))
  .filter((option) => option.value);

export const FamilyForm: React.FC = () => {
  const { setValues, values, errors, setFieldValue, setErrors } =
    useFormikContext<PartnerRegisterationValues>();
  const { data, isLoading } = useGetIsPartner(values.email);

  const isFormFilled = () =>
    !isEmpty(values.name) &&
    !isEmpty(values.phoneNumber) &&
    !isEmpty(values.email) &&
    !isEmpty(values.country) &&
    !isEmpty(values.industry) &&
    values.type === 'family' &&
    !isEmpty(values.occupation);

  const goBack = () =>
    setValues((previous) => ({
      ...previous,
      showNextForm: false,
      showProfileType: true,
      showPasswordForm: false,
      currentStep: '1/5',
    }));

  const handlePhoneInputChange = (value: string | undefined) => {
    if (value && isValidPhoneNumber(value)) {
      const parsedNumber = parsePhoneNumber(value?.toString() ?? '');
      setFieldValue('number', value);
      setFieldValue('phoneNumber', parsedNumber?.nationalNumber);
      setFieldValue('countryCode', parsedNumber?.country);
    }
  };

  const isSubmitting = () => {
    if (data?.isPartner) {
      setErrors({ email: EMAIL_ALREADY_EXIST });
      return;
    }

    if (data?.isGuest) {
      setErrors({ email: EMAIL_ALREADY_EXIST_GUEST });
      return;
    }

    if (!isFormFilled()) {
      toast.error(FILL_REQUIRED_INPUTS, TOAST_DURATION);
      return;
    }

    setValues((previous) => ({
      ...previous,
      showNextForm: false,
      showProfileType: false,
      showPasswordForm: true,
      currentStep: '3/5',
    }));
  };

  return (
    <div className="relative mt-10 border-t-2 border-slate-300">
      <Button
        style="top-[-180px] absolute rounded bg-lightGrey p-1"
        onClick={goBack}
        variant="text"
      >
        <ArrowLeftIcon className="w-5 text-black" />
      </Button>
      <div className="mt-2 mb-2 flex flex-col gap-1">
        <TextField
          className="mt-0"
          name="name"
          type={'text'}
          placeholder="Name"
          label="Family Name*"
        />
        <div className="mt-1">
          <label htmlFor="country">Phone Number *</label>
          <PhoneInput
            placeholder="Phone Number"
            defaultCountry={values.countryCode ? (values.countryCode as CountryCode) : 'US'}
            value={values.number}
            onChange={handlePhoneInputChange}
            name="phoneNumber"
          />
          <span className="text-sm text-red-600">{errors?.number}</span>
        </div>
        <TextField
          className="mt-0 mb-0"
          name="email"
          type={'text'}
          placeholder="email@gmail.com"
          label="Email*"
        />
        <TextField
          className="mb-1"
          name="occupation"
          type={'text'}
          placeholder="Add Occupation"
          label="Occupation*"
        />
        <Dropdown
          fieldName="country"
          options={countryOptions}
          value={{
            label: countryOptions.find((c) => c.value === values.country)?.label ?? '',
            value: values.country ?? '',
          }}
          label="Select Country*"
        />
        <IndustryField industryFieldName="industry" selectedIndustry={values.industry} />
        <TextField
          className=""
          name="nearestBranch"
          type="text"
          placeholder="Nearest Branch"
          label="Nearest Branch"
        />
        <TextField
          className=""
          name="assistantName"
          type="text"
          placeholder="Assistant Name"
          label="Assistant Name"
        />
      </div>
      <div className="mt-5">
        <Button
          isDisabled={isLoading || !isFormFilled()}
          style={`bg-${
            isFormFilled() ? 'gold' : 'gray-300'
          } text-white py-2.5 mx-auto w-full rounded-md`}
          onClick={isSubmitting}
        >
          NEXT
        </Button>
      </div>
    </div>
  );
};
